<template>
	<App title="Swap & Save">
		<div class="row">
			<div class="col">
				<Block type="data">
					<template #header>
						<h3 class="block-title">Swap & Save Details</h3>
					</template>
					<div class="row">
						<div class="col-8">
							<TextBlock label="Title">
								{{ swap_save.title }}
							</TextBlock>
						</div>
						<div class="col-4">
							<TextBlock label="Agent">
								<a class="text-accent1" :href="swap_save.agent?.url.single">{{ swap_save.agent?.fullname }}</a>
							</TextBlock>
						</div>
						<div class="col-4">
							<TextBlock label="Rider">
								<a class="text-accent1" :href="swap_save.rider?.url.single">{{ swap_save.rider?.fullname }}</a>
							</TextBlock>
						</div>
					</div>
					<div class="row">
						<div class="col-4">
							<TextBlock label="Amount">
								{{ swap_save.amount.formatted }}
							</TextBlock>
						</div>
						<div class="col-4">
							<TextBlock label="Income">
								{{ swap_save.income.formatted }}
							</TextBlock>
						</div>
						<div class="col-4">
							<TextBlock label="Interest Rate">
								{{ swap_save.interest.data }}
							</TextBlock>
						</div>
					</div>
					<div class="row">
						<div class="col-4">
							<TextBlock label="Balance">
								{{ swap_save.balance.formatted }}
							</TextBlock>
						</div>
						<div class="col-4">
							<TextBlock label="Earned">
								{{ swap_save.earned.formatted }}
							</TextBlock>
						</div>
					</div>
					<div class="row">
						<div class="col-4">
							<TextBlock label="Created Date">
								{{ $render.date(swap_save.created_at) }}
							</TextBlock>
						</div>
						<div class="col-4">
							<TextBlock label="Maturity Date">
								{{ $render.date(swap_save.yielded_at) }}
							</TextBlock>
						</div>
						<div class="col-4">
							<TextBlock label="Completed Date">
								{{ $render.date(swap_save?.completed_at) }}
							</TextBlock>
						</div>
					</div>
					<div class="row">
						<div class="col-4">
							<TextBlock label="Withdrawn">
								{{ $render.bool(swap_save.hasWithdrawn) }}
							</TextBlock>
						</div>
						<div class="col-4">
							<TextBlock label="Extended">
								{{ $render.bool(swap_save.isExtended) }}
							</TextBlock>
						</div>
						<div class="col-4">
							<TextBlock label="Locked">
								{{ $render.bool(swap_save.isLocked) }}
							</TextBlock>
						</div>
					</div>
					<div v-if="swap_save.isLocked" class="row">
						<div class="col-8">
							<TextBlock label="Lock Remark">
								{{ swap_save.locked_remark }}
							</TextBlock>
						</div>
						<div class="col-4">
							<TextBlock label="Locked date">
								{{ $render.date(swap_save.locked_at) }}
							</TextBlock>
						</div>
					</div>
					<template #footer>
						<button v-if="!swap_save.isCompleted" class="btn btn-tertiary text-danger" @click="onComplete">Complete Plan</button>
						<button v-if="swap_save.isExtendable" class="btn btn-tertiary text-warning" @click="onExtend">Extend Plan</button>
						<button v-if="swap_save.isLocked" class="btn btn-tertiary text-success" @click="onLock($event, 'unlock')">Unlock Plan</button>
						<button v-else class="btn btn-tertiary text-danger" @click="onLock($event, 'lock')">Lock Plan</button>
					</template>
				</Block>
			</div>
		</div>

		<TransactionTable :model="swap_save" />
		<LockerBox ref="locker-modal" :plan="swap_save" />
	</App>
</template>
<script lang="ts">
import type SwapSave from '@/models/SwapSave'
import LockerBox from '@/pages/snippets/LockerBox.vue'
import TransactionTable from '@/pages/snippets/TransactionTable.vue'
import Block from '@/vue/components/Block.vue'
import Card from '@/vue/components/Card.vue'
import TextBlock from '@/vue/components/TextBlock.vue'
import App from '@/vue/templates/App.vue'
import { AxiosError } from 'axios'
import { Component, Prop, Ref, toNative, Vue } from 'vue-facing-decorator'

@Component({
	components: {
		App,
		Card,
		Block,
		TextBlock,
		TransactionTable,
		LockerBox,
	},
})
class Single extends Vue {
	@Prop({ type: Object, required: true })
	readonly swap_save!: SwapSave

	@Ref('locker-modal')
	readonly locker!: InstanceType<typeof LockerBox>

	onComplete(e: Event) {
		e.preventDefault()
		const button = e.target as HTMLButtonElement
		button.disabled = true
		const label = button.innerHTML
		button.innerHTML = '<i class="fas fa-spinner fa-spin"></i>'

		this.$sweet
			.fire({
				title: 'Are You Sure?',
				text: "You won't be able to revert this!",
				icon: 'warning',

				showCancelButton: true,
				confirmButtonColor: '#3085d6',
				cancelButtonColor: '#d33',
				confirmButtonText: 'YES',
			})
			.then((result) => {
				if (result.isConfirmed) {
					this.$http
						.patch(this.route('swap_save.ajax.complete', { swap_save: this.swap_save.slug }))
						.then(() => this.router.reload({ only: ['swap_save'] }))
						.catch((e: AxiosError) => (this.$error(e), (button.disabled = false), (button.innerHTML = label)))
				} else {
					button.disabled = false
					button.innerHTML = label
				}
			})
	}
	onExtend(e: Event) {
		e.preventDefault()
		const button = e.target as HTMLButtonElement
		button.disabled = true
		const label = button.innerHTML
		button.innerHTML = '<i class="fas fa-spinner fa-spin"></i>'

		this.$sweet
			.fire({
				title: 'Are You Sure?',
				text: "You won't be able to revert this!",
				icon: 'warning',

				showCancelButton: true,
				confirmButtonColor: '#3085d6',
				cancelButtonColor: '#d33',
				confirmButtonText: 'YES',
			})
			.then((result) => {
				if (result.isConfirmed) {
					this.$http
						.patch(this.route('swap_save.ajax.extend', { swap_save: this.swap_save.slug }))
						.then(() => this.router.reload({ only: ['swap_save'] }))
						.catch((e: AxiosError) => (this.$error(e), (button.disabled = false), (button.innerHTML = label)))
				} else {
					button.disabled = false
					button.innerHTML = label
				}
			})
	}

	onLock(e: Event, type: string) {
		e.preventDefault()
		const button = e.target as HTMLButtonElement
		button.disabled = true
		const label = button.innerHTML
		button.innerHTML = '<i class="fas fa-spinner fa-spin"></i>'
		console.log('here 0')
		if (type === 'unlock') {
			this.$sweet
				.fire({
					title: 'Are You Sure?',
					text: "You won't be able to revert this!",
					icon: 'warning',

					showCancelButton: true,
					confirmButtonColor: '#3085d6',
					cancelButtonColor: '#d33',
					confirmButtonText: 'YES',
				})
				.then((result) => {
					console.log('Here 1')
					if (result.isConfirmed) {
						console.log('Here 2')
						this.$http
							.patch(this.route('swap_save.ajax.lock', { swap_save: this.swap_save.slug }), { type })
							.then(() => this.router.reload({ only: ['swap_save'] }))
							.catch((e: AxiosError) => (this.$error(e), (button.disabled = false), (button.innerHTML = label)))
					} else {
						button.disabled = false
						button.innerHTML = label
					}
				})
		} else {
			this.locker.modal.onToggleModal()
		}
	}
}
export default toNative(Single)
</script>
